import React, {
	createContext,
	useContext,
	useState,
	useRef,
	useCallback,
	useMemo,
	useEffect,
} from "react";
import { globalHistory } from "@reach/router";
import apiV2 from "api-v2";
import { CancelFileUploadRoute } from "helpers/api/apiRoutes";
import { ApiResponse } from "types/common.types";
import { FileableFile } from "types/storage.types";
import { UploadedFile } from "../types/UploadedFile.type";
import { ChunkUploadExtraData, UploadState } from "../types/ChunkUpload.type";
import { convertFileAbleFileToUploadedFile } from "../utils";
import { logErrorIfDebug, logIfDebug } from "utils/logging";

interface FileUploadContextValue {
	/**
	 * True after the user has confirmed they want to leave and uploads were
	 * cancelled. Resets to false when a new upload begins.
	 */
	hasUserCancelledForNavigation: boolean;
	isUploading: boolean;
	cancelAllUploads: () => void;
	cancelUpload: (uploadId: string) => void;
	clearUploadState: (uploadId: string) => void;
	getUploadState: (uploadId: string) => UploadState;
	/**
	 * Call before closing a modal or dialog that would discard uploads.
	 * Shows a confirmation prompt if uploads are in progress.
	 * Returns true if it is safe to proceed (no uploads, or user confirmed).
	 */
	isSafeToClose: () => boolean;
	uploadFile: (
		uploadFile: UploadedFile,
		extraData?: ChunkUploadExtraData,
		onComplete?: (file: UploadedFile) => void
	) => Promise<void>;
}

interface FileUploadProviderProps {
	children: React.ReactNode;
	chunkSize?: number;
	/**
	 * How often to run cleanup (default: 60_000ms / 1 minute).
	 */
	cleanupIntervalMs?: number;
	/**
	 * Periodically clean up old state for uploads that were never completed
	 * or cleaned up (default: true).
	 */
	enableAggressiveCleanup?: boolean;
	maxRetries?: number;
	/**
	 * Message shown when the user tries to navigate away during an upload.
	 */
	navigationWarningMessage?: string;
	/**
	 * How long to keep completed/cancelled state before cleanup (ms).
	 */
	stateCleanupDelayMs?: number;
	url?: string;
}

// ─── Context ──────────────────────────────────────────────────────────────────

const FileUploadContext = createContext<FileUploadContextValue | null>(null);

// ─── Provider ─────────────────────────────────────────────────────────────────

export const FileUploadProvider: React.FC<FileUploadProviderProps> = ({
	children,
	url = "/file-upload/store",
	chunkSize = 1024 * 1024,
	maxRetries = 3,
	stateCleanupDelayMs = 2000,
	enableAggressiveCleanup = true,
	cleanupIntervalMs = 60_000,
	navigationWarningMessage = "Files are still uploading. Are you sure you want to leave this page and cancel uploads?",
}) => {
	// ── Upload state ─────────────────────────────────────────────────────────

	const [uploadStates, setUploadStates] = useState<Map<string, UploadState>>(
		new Map()
	);
	const controllersRef = useRef<Map<string, AbortController>>(new Map());
	const callbacksRef = useRef<Map<string, (file: UploadedFile) => void>>(
		new Map()
	);
	const timestampsRef = useRef<Map<string, number>>(new Map());

	const isUploading = useMemo(
		() => Array.from(uploadStates.values()).some((s) => s.isUploading),
		[uploadStates]
	);

	// Ref kept in sync so the navigate patch always sees the latest value
	// without needing to be re-installed on every render.
	const isUploadingRef = useRef(isUploading);
	useEffect(() => {
		isUploadingRef.current = isUploading;
	}, [isUploading]);

	// ── Navigation-warning state ──────────────────────────────────────────────

	const [hasUserCancelledForNavigation, setHasUserCancelledForNavigation] =
		useState(false);

	// Set by isSafeToClose after the user confirms, so the navigate patch
	// skips its own prompt for the navigation that immediately follows.
	// Consumed (reset to false) by the patch after each navigation.
	const userHasConfirmedNavigationRef = useRef(false);

	// ── Aggressive cleanup ────────────────────────────────────────────────────

	useEffect(() => {
		if (!enableAggressiveCleanup) { return; }

		const interval = setInterval(() => {
			const now = Date.now();
			const staleThreshold = 5 * 60 * 1000;

			setUploadStates((prev) => {
				const next = new Map(prev);
				const toDelete: string[] = [];

				next.forEach((state, uploadId) => {
					const timestamp = timestampsRef.current.get(uploadId) ?? now;
					if (!state.isUploading && now - timestamp > staleThreshold) {
						toDelete.push(uploadId);
					}
				});

				toDelete.forEach((id) => {
					next.delete(id);
					timestampsRef.current.delete(id);
					controllersRef.current.delete(id);
					callbacksRef.current.delete(id);
				});

				if (toDelete.length > 0) {
					logIfDebug(
						`[FileUpload] Cleaned up ${toDelete.length} stale upload states`
					);
				}

				return next;
			});
		}, cleanupIntervalMs);

		return () => clearInterval(interval);
	}, [enableAggressiveCleanup, cleanupIntervalMs]);

	// ── Core upload helpers ───────────────────────────────────────────────────

	const getUploadState = useCallback(
		(uploadId: string): UploadState =>
			uploadStates.get(uploadId) ?? {
				progress: 0,
				isUploading: false,
				isUploadCompleted: false,
				error: null,
			},
		[uploadStates]
	);

	const updateUploadState = useCallback(
		(uploadId: string, updates: Partial<UploadState>) => {
			setUploadStates((prev) => {
				const next = new Map(prev);
				const current = prev.get(uploadId) ?? {
					progress: 0,
					isUploading: false,
					isUploadCompleted: false,
					error: null,
				};
				next.set(uploadId, { ...current, ...updates });
				timestampsRef.current.set(uploadId, Date.now());
				return next;
			});
		},
		[]
	);

	const clearUploadState = useCallback((uploadId: string) => {
		setUploadStates((prev) => {
			const next = new Map(prev);
			next.delete(uploadId);
			return next;
		});
		controllersRef.current.delete(uploadId);
		callbacksRef.current.delete(uploadId);
		timestampsRef.current.delete(uploadId);
	}, []);

	const cancelUploadOnServer = useCallback(async (uploadId: string) => {
		try {
			await apiV2.post(CancelFileUploadRoute, { uploadId });
		} catch (e) {
			// best-effort
			logErrorIfDebug('Failed to cancel file upload(s).', e);
		}
	}, []);

	const cancelUpload = useCallback(
		(uploadId: string) => {
			const controller = controllersRef.current.get(uploadId);
			if (!controller) { return; }

			controller.abort();
			updateUploadState(uploadId, {
				isUploading: false,
				error: new Error("Upload cancelled by user"),
			});
			setTimeout(() => clearUploadState(uploadId), 1000);
		},
		[updateUploadState, clearUploadState]
	);

	const cancelAllUploads = useCallback(() => {
		const activeIds = Array.from(controllersRef.current.keys());
		if (activeIds.length === 0) { return; }

		logIfDebug(`[FileUpload] Cancelling ${activeIds.length} active uploads`);

		activeIds.forEach((id) => controllersRef.current.get(id)?.abort());

		setUploadStates((prev) => {
			const next = new Map(prev);
			activeIds.forEach((id) => {
				const current = prev.get(id);
				if (current) {
					next.set(id, {
						...current,
						isUploading: false,
						error: new Error("Upload cancelled - page closing"),
					});
					timestampsRef.current.set(id, Date.now());
				}
			});
			return next;
		});

		setTimeout(() => {
			activeIds.forEach((id) => clearUploadState(id));
		}, 1000);
	}, [clearUploadState]);

	// ── Navigation blocking ───────────────────────────────────────────────────
	//
	// We patch globalHistory.navigate rather than using globalHistory.listen.
	// listen() is a notification API — it fires after navigation is committed,
	// making it impossible to block without a navigate-back hack that itself
	// re-triggers the listener. The patch intercepts at the call site, so there
	// is one synchronous gate, one possible confirm dialog, and no re-entrancy.

	useEffect(() => {
		const originalNavigate = globalHistory.navigate;

		globalHistory.navigate = (to, options) => {
			// isSafeToClose already showed a dialog — don't double-prompt,
			// and don't navigate if the user cancelled (it returns false and
			// the caller should bail, but some callers navigate regardless).
			if (isSafeToCloseActiveRef.current) { return; }

			if (isUploadingRef.current && !userHasConfirmedNavigationRef.current) {
				const shouldLeave = window.confirm(navigationWarningMessage);
				if (!shouldLeave) { return; }

				cancelAllUploads();
				setHasUserCancelledForNavigation(true);
			}

			// Consume the flag so future navigations (e.g. after a new upload
			// begins) still prompt correctly.
			userHasConfirmedNavigationRef.current = false;

			originalNavigate(to, options);
		};

		return () => {
			globalHistory.navigate = originalNavigate;
		};
		// cancelAllUploads is stable (useCallback with stable deps).
		// navigationWarningMessage is a primitive prop.
		// Refs don't need to be listed.

	}, [cancelAllUploads, navigationWarningMessage]);

	// Block browser close / refresh — a separate vector from router navigation,
	// handled natively by the browser's beforeunload mechanism.
	useEffect(() => {
		const handleBeforeUnload = (e: BeforeUnloadEvent) => {
			if (!isUploadingRef.current) { return; }
			e.preventDefault();
			e.returnValue = "";
		};

		window.addEventListener("beforeunload", handleBeforeUnload);
		return () => window.removeEventListener("beforeunload", handleBeforeUnload);

	}, []);

	// ── isSafeToClose — modal / dialog guard ─────────────────────────────────
	//
	// For closures that don't trigger router navigation (e.g. dismissing a modal
	// without routing away), call this before proceeding.
	//
	// If the closure DOES trigger navigation afterwards, setting
	// userHasConfirmedNavigationRef here ensures the patched navigate won't
	// prompt a second time.
	const isSafeToCloseActiveRef = useRef(false);

	const isSafeToClose = useCallback((): boolean => {
		if (!isUploadingRef.current) { return true; }

		isSafeToCloseActiveRef.current = true;
		const shouldLeave = window.confirm(navigationWarningMessage);
		isSafeToCloseActiveRef.current = false;

		if (shouldLeave) {
			userHasConfirmedNavigationRef.current = true;
			cancelAllUploads();

			// Synchronously update the ref so the navigate patch
			// won't see an active upload on the same tick.
			isUploadingRef.current = false;
			setHasUserCancelledForNavigation(true);
			return true;
		}

		isSafeToCloseActiveRef.current = false;
		return false;
	}, [cancelAllUploads, navigationWarningMessage]);

	// ── uploadFile ────────────────────────────────────────────────────────────

	const uploadFile = useCallback(
		async (
			file: UploadedFile,
			extraData: ChunkUploadExtraData = {},
			onComplete?: (file: UploadedFile) => void
		) => {
			const raw = file.raw;
			const uploadId = file.uploadId;
			const totalChunks = Math.ceil(raw.size / chunkSize);

			// Reset navigation-cancel flag when a new upload begins.
			setHasUserCancelledForNavigation(false);
			isSafeToCloseActiveRef.current = false;

			if (onComplete) callbacksRef.current.set(uploadId, onComplete);
			controllersRef.current.set(uploadId, new AbortController());

			updateUploadState(uploadId, {
				progress: 0,
				error: null,
				isUploading: true,
				isUploadCompleted: false,
			});

			let finalUploadedFile: UploadedFile | null = null;

			const uploadChunk = async (
				formData: FormData,
				chunkIndex: number,
				retries = 0
			): Promise<ApiResponse<FileableFile>> => {
				const controller = controllersRef.current.get(uploadId);

				try {
					const result = await apiV2.post<ApiResponse<FileableFile>>(
						url,
						formData,
						{
							headers: { "Content-Type": "multipart/form-data" },
							signal: controller?.signal,
						}
					);

					updateUploadState(uploadId, {
						progress: Math.round(((chunkIndex + 1) / totalChunks) * 100),
					});

					return result.data;
				} catch (err: any) {
					if (err?.code === "ERR_CANCELED") {
						controllersRef.current.delete(uploadId);
						await cancelUploadOnServer(uploadId);
						throw err;
					}

					if (retries < maxRetries) {
						return uploadChunk(formData, chunkIndex, retries + 1);
					}

					controllersRef.current.delete(uploadId);
					throw err;
				}
			};

			try {
				for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
					const start = chunkIndex * chunkSize;
					const end = Math.min(start + chunkSize, raw.size);
					const chunk = raw.slice(start, end);

					const formData = new FormData();
					formData.append("file", chunk);
					formData.append("resumableChunkNumber", String(chunkIndex));
					formData.append("resumableTotalChunks", String(totalChunks));
					formData.append("originalName", raw.name);
					formData.append("uploadId", uploadId);

					Object.entries(extraData).forEach(([key, value]) => {
						formData.append(key, String(value));
					});

					const uploadResult = await uploadChunk(formData, chunkIndex);

					if (chunkIndex + 1 === totalChunks) {
						finalUploadedFile = convertFileAbleFileToUploadedFile(
							uploadResult?.data
						);
					}
				}

				updateUploadState(uploadId, {
					isUploading: false,
					isUploadCompleted: true,
				});

				const callback = callbacksRef.current.get(uploadId);
				if (callback && finalUploadedFile) {
					callback(finalUploadedFile);
				}

				setTimeout(() => clearUploadState(uploadId), stateCleanupDelayMs);
			} catch (err) {
				updateUploadState(uploadId, {
					isUploading: false,
					error: err instanceof Error ? err : new Error("Upload failed"),
				});
			}
		},
		[
			chunkSize,
			maxRetries,
			url,
			stateCleanupDelayMs,
			updateUploadState,
			clearUploadState,
			cancelUploadOnServer,
		]
	);

	// ── Context value ─────────────────────────────────────────────────────────

	const value: FileUploadContextValue = {
		uploadFile,
		cancelAllUploads,
		cancelUpload,
		getUploadState,
		clearUploadState,
		isUploading,
		isSafeToClose,
		hasUserCancelledForNavigation,
	};

	return (
		<FileUploadContext.Provider value={value}>
			{children}
		</FileUploadContext.Provider>
	);
};

// ─── Hooks ────────────────────────────────────────────────────────────────────

/**
 * Access the full upload context. Throws if used outside <FileUploadProvider>.
 */
export const useFileUploadContext = (): FileUploadContextValue => {
	const context = useContext(FileUploadContext);
	if (!context) {
		throw new Error(
			"useFileUploadContext must be used within a FileUploadProvider. " +
			"Wrap your app or the relevant subtree with <FileUploadProvider>."
		);
	}
	return context;
};

/**
 * Returns true if a <FileUploadProvider> is present in the tree.
 * Use for components that optionally integrate with uploads.
 */
export const useHasFileUploadContext = (): boolean =>
	useContext(FileUploadContext) !== null;